// src/pages/ApplicationChampionshipMatches/components/MatchSetCurrentRoundModal/index.tsx
import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Flex,
  Grid,
  InputGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { SelectField } from '@/components/SelectField';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { GetParamsObject } from '@/model/query-params';
import { SeasonType } from '@/model';
import { useChampionships } from '@/services/queries/championship';
import { useSeasons } from '@/services/queries/season';
import { useStageById, useStages, usePutStageCurrentRoundMutation } from '@/services/queries/stage';

const I18N_PREFIX = 'pages.application_championship_matches.components.match_current_round_modal';

export interface MatchSetCurrentRoundModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Chamado após salvar com sucesso (ex.: refetch da listagem de jogos). */
  onDone: () => void;
}

/**
 * Modal autossuficiente para definir a rodada atual de uma fase
 * (`stages.current_round`). Tem seu próprio cascade Campeonato → Temporada →
 * Fase, sem depender dos filtros da tabela — o operador não precisa filtrar os
 * jogos só pra marcar a rodada. Usa o endpoint dedicado PUT
 * /stages/:id/current-round, que aceita apenas `{ current_round }`.
 */
export const MatchSetCurrentRoundModal = ({ isOpen, onClose, onDone }: MatchSetCurrentRoundModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const putCurrentRound = usePutStageCurrentRoundMutation();

  const [championshipUuid, setChampionshipUuid] = useState('');
  const [seasonUuid, setSeasonUuid] = useState('');
  const [stageUuid, setStageUuid] = useState('');
  // String pra permitir vazio (= null, "sem rodada destacada").
  const [round, setRound] = useState('');

  // Cascade de lookups — mesmo padrão do MatchGenerateModal.
  const championshipsParams = useMemo(
    () => GetParamsObject({} as Record<string, string>, 1, 200, 'asc', 'name'),
    [],
  );
  const { data: championshipsData } = useChampionships(championshipsParams);

  const seasonsParams = useMemo(
    () =>
      championshipUuid
        ? GetParamsObject({ championship_uuid: championshipUuid } as Record<string, string>, 1, 200, 'desc', 'year')
        : undefined,
    [championshipUuid],
  );
  const { data: seasonsData } = useSeasons(seasonsParams);

  const stagesParams = useMemo(
    () =>
      seasonUuid
        ? GetParamsObject({ season_uuid: seasonUuid } as Record<string, string>, 1, 200, 'asc', 'stage_order')
        : undefined,
    [seasonUuid],
  );
  const { data: stagesData } = useStages(stagesParams);

  // Detalhe da fase selecionada — fonte confiável de current_round para o
  // pré-preenchimento (a listagem nem sempre traz o campo).
  const { data: selectedStage } = useStageById(stageUuid || '');

  // Sempre que a fase muda, espelha a rodada atual gravada no input.
  useEffect(() => {
    if (!stageUuid) {
      setRound('');
      return;
    }
    if (selectedStage?.uuid === stageUuid) {
      setRound(selectedStage.current_round != null ? String(selectedStage.current_round) : '');
    }
  }, [stageUuid, selectedStage?.uuid, selectedStage?.current_round]);

  const resetAll = () => {
    setChampionshipUuid('');
    setSeasonUuid('');
    setStageUuid('');
    setRound('');
  };

  const handleClose = () => {
    resetAll();
    onClose();
  };

  const handleChampionshipChange = (value: string) => {
    setChampionshipUuid(value);
    setSeasonUuid('');
    setStageUuid('');
    setRound('');
  };

  const handleSeasonChange = (value: string) => {
    setSeasonUuid(value);
    setStageUuid('');
    setRound('');
  };

  const currentRoundLabel =
    selectedStage?.current_round != null
      ? t(`${I18N_PREFIX}.current_value`, { round: selectedStage.current_round })
      : t(`${I18N_PREFIX}.current_value_empty`);

  const handleSave = () => {
    if (!stageUuid) return;
    const trimmed = round.trim();
    const parsed = trimmed === '' ? null : Number(trimmed);

    if (parsed != null && (!Number.isInteger(parsed) || parsed < 1)) {
      toast({ status: 'error', title: t(`${I18N_PREFIX}.toasts.invalid_round`) });
      return;
    }

    putCurrentRound.mutate(
      { id: stageUuid, payload: { current_round: parsed } },
      {
        onSuccess: () => {
          toast({ status: 'success', title: t(`${I18N_PREFIX}.toasts.save_success`) });
          // Atualiza o detalhe da fase (usado no filtro de rodadas da página).
          queryClient.invalidateQueries({ queryKey: ['GET_STAGE_BY_ID', stageUuid] });
          resetAll();
          onDone();
        },
        onError: (error: unknown) => {
          toast({
            status: 'error',
            title: (error as Error)?.message || t(`${I18N_PREFIX}.toasts.save_error`),
          });
        },
      },
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl' isCentered scrollBehavior='inside'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex align='center' gap={2}>
            <ChakraFeatherIcon name='flag' color='brand.500' />
            <Text fontSize='medium' fontWeight='bold'>
              {t(`${I18N_PREFIX}.title`)}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex direction='column' gap={4}>
            <Text fontSize='sm' color='gray.600'>
              {t(`${I18N_PREFIX}.description`)}
            </Text>

            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_championship`)}</Text>
                <SelectField value={championshipUuid} onChange={event => handleChampionshipChange(event.target.value)}>
                  <option value=''>{t(`${I18N_PREFIX}.placeholder_select`)}</option>
                  {(championshipsData?.data ?? []).map(champ => (
                    <option key={champ.uuid} value={champ.uuid}>
                      {champ.name}
                    </option>
                  ))}
                </SelectField>
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_season`)}</Text>
                <SelectField
                  value={seasonUuid}
                  isDisabled={!championshipUuid}
                  onChange={event => handleSeasonChange(event.target.value)}
                >
                  <option value=''>{t(`${I18N_PREFIX}.placeholder_select`)}</option>
                  {(seasonsData?.data ?? []).map(season => (
                    <option key={season.uuid} value={season.uuid}>
                      {season.year}
                      {season.type === SeasonType.HYBRID ? ` · ${t(`${I18N_PREFIX}.season_type_hybrid`)}` : ''}
                      {season.type === SeasonType.KNOCKOUT ? ` · ${t(`${I18N_PREFIX}.season_type_knockout`)}` : ''}
                      {season.type === SeasonType.LEAGUE ? ` · ${t(`${I18N_PREFIX}.season_type_league`)}` : ''}
                    </option>
                  ))}
                </SelectField>
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_stage`)}</Text>
                <SelectField
                  value={stageUuid}
                  isDisabled={!seasonUuid}
                  onChange={event => setStageUuid(event.target.value)}
                >
                  <option value=''>{t(`${I18N_PREFIX}.placeholder_select`)}</option>
                  {(stagesData?.data ?? []).map(stage => (
                    <option key={stage.uuid} value={stage.uuid}>
                      {stage.group_name ? `${stage.name} · ${stage.group_name}` : stage.name}
                    </option>
                  ))}
                </SelectField>
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_round`)}</Text>
                <NumberInput
                  min={1}
                  value={round}
                  isDisabled={!stageUuid}
                  onChange={valueAsString => setRound(valueAsString)}
                >
                  <NumberInputField placeholder={t(`${I18N_PREFIX}.field_round_placeholder`)} />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </InputGroup>
            </Grid>

            {stageUuid ? (
              <Flex
                p={3}
                bg='gray.50'
                border='1px solid'
                borderColor='gray.200'
                borderRadius='md'
                align='center'
                gap={2}
              >
                <ChakraFeatherIcon name='info' boxSize='16px' color='gray.500' />
                <Text fontSize='sm' color='gray.600'>
                  {currentRoundLabel}
                </Text>
              </Flex>
            ) : null}

            <Text fontSize='xs' color='gray.500'>
              {t(`${I18N_PREFIX}.helper`)}
            </Text>
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Flex w='100%' justifyContent='flex-end' gap={3}>
            <Button variant='outline' color='gray.600' onClick={handleClose} isDisabled={putCurrentRound.isPending}>
              {t(`${I18N_PREFIX}.cancel_button`)}
            </Button>
            <Button
              bg='brand.500'
              color='white'
              _hover={{ bg: 'brand.600' }}
              px={10}
              onClick={handleSave}
              isLoading={putCurrentRound.isPending}
              isDisabled={!stageUuid}
            >
              {t(`${I18N_PREFIX}.save_button`)}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
